import React, { useEffect, useState } from "react";
import axios from "axios";
import HiddenTKReport from "./HiddenTKReport";
import SurveyInviteWidget from "./SurveyInviteWidget";

// ── Band → colour map (matches API band values: "high" | "medium" | "low") ────
const bandColor = (band) => {
  if (!band) return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  switch (band.toLowerCase()) {
    case "high":   return { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" }; // green
    case "medium": return { bg: "#dbeafe", color: "#3f7cac", border: "#bfdbfe" }; // blue
    case "low":    return { bg: "#fef3c7", color: "#d88a1f", border: "#fde68a" }; // amber
    default:       return { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
  }
};

// ── Per-mode accent colours (matching TKResults.jsx) ─────────────────────────
const MODE_COLORS = {
  competing:     "#A80D2C",
  collaborative: "#1A6B3C",
  compromising:  "#7B3F9E",
  avoiding:      "#D79A1E",
  accommodating: "#1B6FA0",
};

// ── Dominant-mode badge colour ────────────────────────────────────────────────
const dominantColor = (key) => {
  const accent = MODE_COLORS[key] || "#6b7280";
  const alphaMap = {
    competing:     { bg: "#fee2e2", color: "#A80D2C", border: "#fecaca" },
    collaborative: { bg: "#dcfce7", color: "#1A6B3C", border: "#bbf7d0" },
    compromising:  { bg: "#ede9fe", color: "#7B3F9E", border: "#ddd6fe" },
    avoiding:      { bg: "#fef3c7", color: "#D79A1E", border: "#fde68a" },
    accommodating: { bg: "#dbeafe", color: "#1B6FA0", border: "#bfdbfe" },
  };
  return alphaMap[key] || { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
};

// ── TK conflict mode display config (API keys) ────────────────────────────────
// NOTE: API uses "collaborative" (not "collaborating")
const MODES = [
  { key: "competing",     label: "Competing"     },
  { key: "collaborative", label: "Collaborative" },
  { key: "compromising",  label: "Compromising"  },
  { key: "avoiding",      label: "Avoiding"      },
  { key: "accommodating", label: "Accommodating" },
];

export default function GlobalTKResponses({ surveyId }) {
  const [tkData,         setTkData]         = useState([]);
  const [loading,        setLoading]        = useState(true);

  // ── Filters ──
  const [filterName,        setFilterName]        = useState("");
  const [filterEmail,       setFilterEmail]       = useState("");
  const [filterDominant,    setFilterDominant]    = useState("");
  const [filterCompany,     setFilterCompany]     = useState("");
  const [filterDept,        setFilterDept]        = useState("");
  const [filterStartDate,   setFilterStartDate]   = useState("");
  const [filterEndDate,     setFilterEndDate]     = useState("");

  // ── PDF state ──
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ================= FETCH =================
  useEffect(() => {
    if (surveyId) fetchTKResults();
  }, [surveyId]);

  const fetchTKResults = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/response/calculate-score/tk/all`,
        { params: { survey_id: surveyId } }
      );
      setTkData(res.data.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch TK results", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  // Collect all unique dominant-mode values across all respondents
  const uniqueDominantModes = [
    ...new Set(
      tkData.flatMap((u) => u.scores?.dominant_mode || [])
    ),
  ].sort();

  const uniqueCompanies = [
    ...new Set(tkData.map((u) => (u?.company || "").trim()).filter(Boolean)),
  ].sort();

  const uniqueDepts = [
    ...new Set(tkData.map((u) => (u?.department || "").trim()).filter(Boolean)),
  ].sort();

  const filteredData = tkData.filter((user) => {
    const name    = (user?.name       || "").toLowerCase();
    const email   = (user?.email      || "").toLowerCase();
    const company = (user?.company    || "").trim().toLowerCase();
    const dept    = (user?.department || "").trim().toLowerCase();
    const domKeys = user.scores?.dominant_mode || [];

    const matchName     = name.includes(filterName.toLowerCase());
    const matchEmail    = email.includes(filterEmail.toLowerCase());
    const matchCompany  = filterCompany === "" || company === filterCompany.toLowerCase();
    const matchDept     = filterDept    === "" || dept    === filterDept.toLowerCase();
    const matchDominant = filterDominant === "" || domKeys.includes(filterDominant);

    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(user.submission_time);
      submissionDate.setHours(0, 0, 0, 0);

      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchCompany && matchDept && matchDominant && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterDominant("");
    setFilterCompany("");
    setFilterDept("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ── Summary counts per dominant-mode key ──
  // A respondent can have multiple dominant modes (e.g. ["collaborative", "accommodating"])
  const dominantCounts = tkData.reduce((acc, u) => {
    (u.scores?.dominant_mode || []).forEach((k) => {
      acc[k] = (acc[k] || 0) + 1;
    });
    return acc;
  }, {});

  // ================= PDF DOWNLOAD =================
  // HiddenTKReport expects scores with key "collaborating" (not "collaborative")
  const handleDownloadPDF = (user, rowIndex) => {
    setIsGeneratingRow(rowIndex);

    const modes = user.scores?.modes || {};

    const scores = {
      competing:     modes.competing?.score     ?? 0,
      collaborating: modes.collaborative?.score ?? 0,  // renamed for HiddenTKReport
      compromising:  modes.compromising?.score  ?? 0,
      avoiding:      modes.avoiding?.score      ?? 0,
      accommodating: modes.accommodating?.score ?? 0,
    };

    // Remap "collaborative" → "collaborating" for HiddenTKReport conventions
    const dominantModes = (user.scores?.dominant_mode || []).map((k) =>
      k === "collaborative" ? "collaborating" : k
    );

    const dominantLabels = dominantModes
      .map((k) => (k === "collaborating" ? "Collaborative" : MODES.find((m) => m.key === k)?.label ?? k))
      .join(" & ");

    setDownloadData({
      userInfo: {
        name:  user?.name  || "Participant",
        email: user?.email || "",
        phone: user?.phone || "",
      },
      scores,
      insightText: `Your conflict mode profile indicates a primary tendency towards ${dominantLabels}. Each of the five modes has its appropriate context — your repertoire reflects the mix of situations you encounter.`,
      dominantModes,
      submission_time: user.submission_time,
    });
  };

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ================= UI =================
  if (loading) return <div className="loading-state">Loading TK Conflict Mode data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>
       <SurveyInviteWidget surveyId={surveyId} />

      {/* Hidden PDF renderer */}
      <HiddenTKReport
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <h3 style={{ margin: 0 }}>⚔️ TK Conflict Mode Responses ({filteredData.length})</h3>
        <span style={{
          background: "#A80D2C", color: "white",
          padding: "5px 10px", borderRadius: "4px",
          fontSize: "12px", fontWeight: "bold",
        }}>
          Global Public View
        </span>
      </div>

      {/* ── Dominant-mode summary pills (clickable to filter) ── */}
      {tkData.length > 0 && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: "8px",
          marginBottom: "16px",
        }}>
          {Object.entries(dominantCounts).map(([modeKey, count]) => {
            const dc    = dominantColor(modeKey);
            const label = MODES.find((m) => m.key === modeKey)?.label ?? modeKey;
            return (
              <span
                key={modeKey}
                onClick={() => setFilterDominant(filterDominant === modeKey ? "" : modeKey)}
                title="Click to filter by this dominant mode"
                style={{
                  background:    dc.bg,
                  color:         dc.color,
                  border:        `1px solid ${dc.border}`,
                  padding:       "4px 12px",
                  borderRadius:  "999px",
                  fontSize:      "12px",
                  fontWeight:    "700",
                  cursor:        "pointer",
                  outline:       filterDominant === modeKey ? `2px solid ${dc.color}` : "none",
                  outlineOffset: "2px",
                }}
              >
                {label}: {count}
              </span>
            );
          })}
          <span style={{ fontSize: "12px", color: "#888", alignSelf: "center", marginLeft: "4px" }}>
            ← click to filter
          </span>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>
        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map(c => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Department</label>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Departments</option>
            {uniqueDepts.map(d => (
              <option key={d} value={d.toLowerCase()}>{d}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Dominant Mode</label>
          <select
            value={filterDominant}
            onChange={(e) => setFilterDominant(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Modes</option>
            {uniqueDominantModes.map((k) => (
              <option key={k} value={k}>
                {MODES.find((m) => m.key === k)?.label ?? k}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Start Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>End Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{
              padding: "8px 15px", background: "#A81026", color: "white",
              border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {tkData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No TK Conflict Mode responses found.</div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No responses match your current filters.</div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company / Dept</th>
                <th style={{ padding: "12px", minWidth: "340px" }}>
                  Conflict Mode Scores (/ 12)
                </th>
                <th style={{ padding: "12px", minWidth: "130px", background: "#fdf6e3" }}>
                  ⚔️ Dominant Mode
                </th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((user, index) => {
                const modes        = user.scores?.modes        || {};
                const dominantKeys = user.scores?.dominant_mode || [];
                const isGenerating = isGeneratingRow === index;

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* ── Date ── */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {user.submission_time
                        ? <>
                            {new Date(user.submission_time).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}<br />
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {new Date(user.submission_time).toLocaleTimeString("en-US", {
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </span>
                          </>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      }
                    </td>

                    {/* ── Participant ── */}
                    <td style={{ padding: "12px" }}>
                      <b>{user?.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {user?.email}<br />
                        {user?.phone || ""}
                      </span>
                    </td>

                    {/* ── Company / Dept ── */}
                    <td style={{ padding: "12px" }}>
                      {user?.company
                        ? <><b>{user.company}</b><br /></>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                      {user?.department
                        ? <span style={{ fontSize: "13px", color: "#555" }}>{user.department}</span>
                        : null
                      }
                    </td>

                    {/* ── Mode score pills ── */}
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {MODES.map(({ key, label }) => {
                          const modeData = modes[key];
                          const score    = modeData?.score ?? null;
                          const band     = modeData?.band  ?? null;
                          const isTop    = dominantKeys.includes(key);
                          const c        = bandColor(band);
                          return (
                            <span
                              key={key}
                              title={`${label}: ${score ?? "—"}/12 · Band: ${band ?? "—"}`}
                              style={{
                                background:   c.bg,
                                color:        c.color,
                                border:       `${isTop ? "2px" : "1px"} solid ${c.border}`,
                                padding:      "2px 8px",
                                borderRadius: "4px",
                                fontSize:     "12px",
                                fontWeight:   isTop ? "800" : "600",
                                whiteSpace:   "nowrap",
                              }}
                            >
                              {label}: {score ?? "—"}
                              {band && (
                                <span style={{ fontWeight: "500", marginLeft: "4px", opacity: 0.8 }}>
                                  ({band})
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </td>

                    {/* ── Dominant Mode badge(s) ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      {dominantKeys.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          {dominantKeys.map((k) => {
                            const dc    = dominantColor(k);
                            const label = MODES.find((m) => m.key === k)?.label ?? k;
                            return (
                              <span
                                key={k}
                                style={{
                                  background:   dc.bg,
                                  color:        dc.color,
                                  border:       `1px solid ${dc.border}`,
                                  padding:      "3px 10px",
                                  borderRadius: "999px",
                                  fontSize:     "12px",
                                  fontWeight:   "800",
                                  whiteSpace:   "nowrap",
                                  display:      "inline-block",
                                }}
                              >
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      )}
                    </td>

                    {/* ── Download ── */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(user, index)}
                        disabled={isGenerating}
                        style={{
                          background:   isGenerating ? "#95a5a6" : "#A81026",
                          color:        "white",
                          border:       "none",
                          padding:      "8px 12px",
                          borderRadius: "6px",
                          cursor:       isGenerating ? "not-allowed" : "pointer",
                          fontWeight:   "bold",
                          fontSize:     "12px",
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          margin:       "0 auto",
                          transition:   "background 0.3s",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}